import { useEffect, useState, useMemo } from "react";
import { Puck, createUsePuck } from "@puckeditor/core";
import { useNavigate, useParams } from "react-router-dom";
import { config } from "../Config";
import axiosClient from "../axios-client";
import { useStateContext } from "../Providers/ContextProvider";
import { Oval } from "react-loader-spinner";
import { Layout, Save, ArrowRight, Search, Settings } from "lucide-react";

// Initialize the Puck hook to access internal state
const usePuck = createUsePuck();

const Builder = () => {
  const { data, setData } = useStateContext();
  const navigate = useNavigate();
  const { pageId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const GetContent = async () => {
    setIsLoading(true);
    try {
      const res = await axiosClient.get(`/pages/${pageId}`);
      setData(res.data.data || { content: [], root: {}, zones: {} });
    } catch (error) {
      if (error.response?.status === 404) navigate("/");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (pageId) GetContent();
  }, [pageId]);

  const handleSave = async (puckData) => {
    setIsSaving(true);
    try {
      await axiosClient.post(`/pages/${pageId}`, puckData);
      // Optional: You could show a toast here instead of an alert
      console.log("Saved successfully");
    } catch (error) {
      console.error("Save error", error);
      alert("خطأ أثناء الحفظ");
    } finally {
      setIsSaving(false);
    }
  };

  const overrides = useMemo(
    () => ({
      // 1. HEADER OVERRIDE: Custom title and Save button
      header: ({ children }) => {
        const { appState } = usePuck(); // Access puck data directly

        return (
          <header className="flex items-center justify-between px-6 py-3 bg-white border-b border-zinc-200 z-[1000] shadow-sm">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-9 h-9 bg-blue-600 rounded-lg text-white shadow-md">
                <Layout size={20} />
              </div>
              <div>
                <h1 className="text-sm font-bold text-zinc-900 leading-none">
                  محرر المحتوى
                </h1>
                <p className="text-[10px] text-zinc-400 mt-1 font-mono uppercase tracking-tighter">
                  PAGE_REF: {pageId}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/")}
                className="px-4 py-2 text-xs font-medium text-zinc-500 hover:bg-zinc-100 rounded-md transition-colors flex items-center gap-2"
              >
                <ArrowRight size={14} />
                خروج
              </button>

              <button
                disabled={isSaving}
                onClick={() => handleSave(appState.data)}
                className={`flex items-center gap-2 px-5 py-2 text-xs font-bold rounded-lg transition-all shadow-sm ${
                  isSaving
                    ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700 active:scale-95"
                }`}
              >
                {isSaving ? (
                  <Oval
                    height="14"
                    width="14"
                    color="#94a3b8"
                    secondaryColor="#e2e8f0"
                  />
                ) : (
                  <Save size={14} />
                )}
                {isSaving ? "جاري الحفظ..." : "حفظ التغييرات"}
              </button>
            </div>
          </header>
        );
      },

      // 2. DRAWER OVERRIDE: Improve component list visuals
      drawerItem: ({ name }) => (
        <div className="group relative p-3 mb-2 bg-zinc-50 border border-zinc-200 rounded-xl hover:border-blue-500 hover:bg-white hover:shadow-md transition-all cursor-grab active:cursor-grabbing">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white border border-zinc-200 rounded-lg flex items-center justify-center text-zinc-400 group-hover:text-blue-500 group-hover:border-blue-100 transition-colors">
              <Settings size={14} />
            </div>
            <span className="text-xs font-semibold text-zinc-600 group-hover:text-zinc-900 leading-tight">
              {name}
            </span>
          </div>
        </div>
      ),

      // 3. FIELD TYPES OVERRIDE: Global style for inputs
      fieldTypes: {
        text: ({ name, onChange, value, label }) => (
          <div className="py-2">
            <label className="block text-[11px] font-bold text-zinc-400 uppercase mb-1.5 tracking-wider">
              {label}
            </label>
            <input
              defaultValue={value}
              name={name}
              onChange={(e) => onChange(e.currentTarget.value)}
              className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              placeholder={`Enter ${name}...`}
            />
          </div>
        ),
        select: ({ name, onChange, value, label, options }) => (
          <div className="py-2">
            <label className="block text-[11px] font-bold text-zinc-400 uppercase mb-1.5 tracking-wider">
              {label}
            </label>
            <select
              value={value}
              onChange={(e) => onChange(e.currentTarget.value)}
              className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all appearance-none"
            >
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ),
      },
    }),
    [pageId, isSaving, navigate],
  );

  if (isLoading) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-white">
        <Oval
          visible={true}
          height="50"
          width="50"
          color="#2563eb"
          secondaryColor="#f4f4f5"
        />
        <p className="mt-4 text-zinc-400 text-sm font-medium animate-pulse">
          تجهيز المحرر...
        </p>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-zinc-50 font-sans overflow-hidden">
      {pageId ? (
        <Puck
          config={config}
          data={data}
          onPublish={handleSave}
          overrides={overrides}
          headerPath="Content"
        />
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="p-6 bg-red-50 text-red-500 rounded-xl border border-red-100 text-sm font-bold">
            Page ID is required to launch the builder.
          </div>
        </div>
      )}
    </div>
  );
};

export default Builder;
